import { useCallback, useMemo } from 'react'

import { useSplitEnabled } from '@buffer-mono/features'

import type { Service } from '../graphqlEnums'

import { getMixedMediaCapability } from './mixedMediaCapability'
import type { MixedMediaCapability } from './mixedMediaCapability'

export type UseMixedMediaCapability = {
  /** The org-level `mixed-media` Split flag, resolved (false until `isReady`). */
  orgEnabled: boolean
  /** Whether the Split SDK has resolved the `mixed-media` treatment yet. */
  isReady: boolean
  /** Stable per-service capability lookup, delegating to {@link getMixedMediaCapability}. */
  forService: (service: Service) => MixedMediaCapability
}

/**
 * Thin React wiring over {@link getMixedMediaCapability}: resolves the
 * `mixed-media` Split flag, then exposes a stable per-service lookup.
 * Holds no capability logic of its own.
 */
export function useMixedMediaCapability(): UseMixedMediaCapability {
  const { isEnabled, isReady } = useSplitEnabled('mixed-media')
  const orgEnabled = isReady && isEnabled

  const forService = useCallback(
    (service: Service) => getMixedMediaCapability({ orgEnabled, service }),
    [orgEnabled],
  )

  return useMemo(
    () => ({ orgEnabled, isReady, forService }),
    [orgEnabled, isReady, forService],
  )
}
